import React, { useState } from 'react';
import { FiX, FiSend } from 'react-icons/fi';
import api from '../../api/api';

const CreateTicketModal = ({ isOpen, onClose, onTicketCreated }) => {
  const [formData, setState] = useState({
    subject: '',
    priority: 'Medium',
    description: '',
    recipientType: 'admin'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState(prev => ({ ...prev, [name]: value }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      console.log('[CreateTicket] Submitting ticket:', {
        subject: formData.subject,
        description: formData.description,
        priority: formData.priority
      });

      // Send the ticket data to the backend
      const response = await api.post('/support/tickets', {
        subject: formData.subject,
        description: formData.description,
        priority: formData.priority
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('[CreateTicket] Response received:', response.data);

      // Check outer success flag
      if (response.data && response.data.success) {
        alert('Support ticket created successfully! An admin will respond shortly.');
        // Reset form data
        setState({
          subject: '',
          priority: 'Medium',
          description: '',
          recipientType: 'admin'
        });
        // Close the modal
        onClose();
        // Call the callback to refresh tickets
        if (onTicketCreated && response.data.data) {
          // Access ticket from nested data structure
          const ticket = response.data.data.ticket;
          if (ticket) {
            onTicketCreated(ticket);
          }
        }
      } else {
        setError('Failed to create ticket. Please try again.');
      }
    } catch (err) {
      console.error('[CreateTicket] Error creating ticket:', err);
      console.error('[CreateTicket] Error response:', err.response);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to create ticket. Please try again.';
      console.error('[CreateTicket] Error message:', errorMsg);
      setError(errorMsg);
      alert('Error: ' + errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white border border-black p-8 w-full max-w-lg">
        <div className="flex justify-between items-center mb-6 border-b border-black pb-3">
          <h2 className="text-xl font-bold text-black">Create New Support Ticket</h2>
          <button onClick={onClose} className="text-black hover:text-gray-600"><FiX size={24} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="space-y-5">
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-black mb-2">Subject</label>
              <input 
                type="text" 
                id="subject" 
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="block w-full border-b-2 border-gray-300 py-2 px-1 focus:border-yellow-400 focus:outline-none transition-colors" 
                required
              />
            </div>
            <div>
              <label htmlFor="recipientType" className="block text-sm font-medium text-black mb-2">Send To</label>
              <select 
                id="recipientType" 
                name="recipientType"
                value={formData.recipientType}
                onChange={handleChange}
                className="block w-full border-b-2 border-gray-300 py-2 px-1 focus:border-yellow-400 focus:outline-none appearance-none bg-white transition-colors"
              >
                <option value="admin">Platform Admin</option>
                <option value="support">Support Team</option>
              </select>
            </div>
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-black mb-2">Priority</label>
              <select 
                id="priority" 
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="block w-full border-b-2 border-gray-300 py-2 px-1 focus:border-yellow-400 focus:outline-none appearance-none bg-white transition-colors"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-black mb-2">Description</label>
              <textarea 
                id="description" 
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="5" 
                className="block w-full border-b-2 border-gray-300 py-2 px-1 focus:border-yellow-400 focus:outline-none transition-colors"
                required
              ></textarea>
            </div>
          </div>
          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          <div className="mt-8 pt-4 border-t border-black flex justify-end gap-4">
            <button 
              type="button" 
              onClick={onClose} 
              disabled={isSubmitting}
              className="border border-black text-black font-medium py-2 px-6 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-black text-white font-medium py-2 px-6 hover:bg-gray-800 flex items-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <FiSend size={16} />
                  Submit Ticket
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTicketModal;
